import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { DatePicker } from 'antd';

import styles from './styles.module.scss';
import stylesDashboard from '../../styles/dashboard.module.scss';

import { PhotosTypeSwitchExtended } from '../PhotosTypeSwitch/index.jsx';
import { useGetSensorImagesQuery } from '../../store/api/sensors';
import { DateFormat, DateLlFormat } from '../../constants/data';
import { useGetNotesQuery } from '../../store/api/notes';
import { Loading } from '../index.jsx';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { Card } from '../Card/index.jsx';
import Panel from '../Panel/index.jsx';
import { NotesPanel } from '../Notes/index.jsx';
import { FilterOrderButton, FilterPhoto } from '../Filters/index.jsx';
import { updatePhotoSorting } from '../../store/slices/sensors';
import { ImageZoomModals } from '../ImageZoomModals/index.jsx';
import { getCropName } from '../../utils/crops';

const { RangePicker } = DatePicker;

const Photo = ({ t, crop }) => {
  const dispatch = useDispatch();
  const {
    selectedSensor,
    photosType,
    sortingPhoto: { dir },
  } = useSelector((state) => state.sensors);
  const { widthViewport } = useViewport();
  const { crops } = useSelector((state) => state.catalog);

  const {
    data: images,
    isSuccess: isImagesSuccess,
    isFetching: isImagesLoading,
  } = useGetSensorImagesQuery({
    location_id: selectedSensor,
    ordering: 'date',
  });
  const { data: notes, isSuccess: isNotesSuccess } = useGetNotesQuery(
    { location_id: selectedSensor, ordering: 'created_at' },
    { skip: !selectedSensor, refetchOnMountOrArgChange: true },
  );

  const [photoList, setPhotoList] = useState([]);
  const [photoFiltered, setPhotoFiltered] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fullImage, setFullImage] = useState(false);
  const [activeNotesPanel, setActiveNotesPanel] = useState(false);
  const [notesData, setNotesData] = useState({ sensor: '', notes: '', data: '', locationId: '' });
  const [activeFilterPanel, setActiveFilterPanel] = useState(false);

  // Sort
  const sortList = (list) => {
    return list && list.length
      ? list.sort((a, b) => (dir === 'des' ? dayjs(b.date).diff(dayjs(a.date)) : dayjs(a.date).diff(dayjs(b.date))))
      : [];
  };

  // Add notes
  const mergeListWithNotes = (list) => {
    if (!notes) return list;
    return list.map((item) => ({
      ...item,
      notes: notes.filter((note) => dayjs(note.date).isSame(item.date, 'day')) || [],
    }));
  };

  // Change period
  const onChangeRange = (value) => {
    if (value?.length === 2) {
      const [start, end] = value;

      let filtered = photoList.filter((item) => dayjs(item.date).isBetween(start, end, 'day', '[]'));

      filtered = mergeListWithNotes([...filtered]);
      filtered = sortList([...filtered]);
      setPhotoFiltered(filtered);
    } else {
      setPhotoFiltered(photoList);
    }
  };

  // Get disabled dates
  const disabledDate = (current) => {
    return !photoList.some((item) => dayjs(item.date).isSame(current, 'day'));
  };

  // Open full image
  const onOpenFullImageModal = ({ status, image }) => {
    setModalOpen(status);
    setFullImage(image);
    if (status && widthViewport < BreakpointMobileUp) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  const onOpenNotesPanel = (data) => {
    const { sensor, notes, date, locationId } = data;
    setNotesData({ sensor, notes, date, locationId });
    setActiveNotesPanel(true);
  };

  const onChangeOrder = () => {
    if (widthViewport > BreakpointMobile) {
      dispatch(updatePhotoSorting({ dir: dir === 'asc' ? 'des' : 'asc' }));
    } else {
      setActiveFilterPanel(true);
    }
  };

  useEffect(() => {
    if (!isNotesSuccess || !isImagesSuccess) {
      return;
    }
    if (images) {
      let list;
      switch (photosType) {
        case 'night':
          list = images.imagesNight;
          break;
        case 'day':
          list = images.imagesDay;
          break;
        default:
          list = images.imagesNDVI;
      }

      setPhotoList(list);
      list = mergeListWithNotes([...list]);
      list = sortList([...list]);
      setPhotoFiltered(list);
    }
  }, [images, notes, isImagesSuccess, isNotesSuccess, photosType, dir]);

  if (isImagesLoading) {
    return <Loading />;
  }

  return (
    <div className={`${styles.photo} ${stylesDashboard.sensorContainerInner} custom-scroll`}>
      <div className={styles.photoFilter}>
        <div className={styles.photoFilterCell}>
          {photoList.length > 0 && (
            <RangePicker
              format={DateFormat}
              inputReadOnly={true}
              defaultValue={[
                dayjs(photoList[0].date, DateFormat),
                dayjs(photoList[photoList.length - 1].date, DateFormat),
              ]}
              size={widthViewport < BreakpointMobileUp ? 'large' : null}
              rootClassName={'custom-date-picker custom-date-picker--range'}
              allowClear={false}
              disabledDate={disabledDate}
              onChange={onChangeRange}
            />
          )}
        </div>
        <div className={styles.photoFilterCell}>
          {widthViewport > BreakpointMobile && <PhotosTypeSwitchExtended />}
          <FilterOrderButton onChangeOrder={onChangeOrder} isCustomOrder={true} arrowPosition={dir} t={t} />
        </div>
      </div>

      <div className={styles.cards}>
        {photoFiltered.length > 0 &&
          photoFiltered.map((item, index) => {
            return (
              <Card
                key={`photo${index}`}
                image={item.url}
                temperature={item.temperature}
                gdd={item.gdd}
                dewDuration={item.dewDuration}
                ndvi={item.ndvi}
                date={item.date}
                notesLength={item.notes.length}
                showDownloadButton={true}
                onClick={() => onOpenFullImageModal({ status: true, image: item.url })}
                onClickNotes={() => onOpenNotesPanel(item)}
                onClickExpandImage={() => onOpenFullImageModal({ status: true, image: item.url })}
                t={t}
              />
            );
          })}
      </div>

      {/*Full image modal*/}
      <ImageZoomModals
        fullImage={fullImage}
        modalOpen={modalOpen}
        onCloseModal={() => onOpenFullImageModal({ status: false, image: '' })}
      />

      {/*Filter*/}
      {activeFilterPanel && widthViewport < BreakpointMobileUp && (
        <Panel title={t('sensor.photoFilterTitle')} setActivePanel={() => setActiveFilterPanel(false)}>
          <FilterPhoto t={t} />
        </Panel>
      )}

      {activeNotesPanel && (
        <Panel
          title={t('notes.title')}
          subtitle={
            <div className="delimited-text">
              {!!(notesData.sensor && notesData.sensor.name) && <span>{notesData.sensor.name}</span>}
              {crop && <span className="text-capitalize">{getCropName({ t, id: crop.replace(/_/g, ' ') })}</span>}
              <span>{dayjs(notesData.date).format(DateLlFormat)}</span>
            </div>
          }
          setActivePanel={() => setActiveNotesPanel(false)}
        >
          <NotesPanel
            notes={notesData.notes}
            color={crop ? crops.filter((item) => item.id === crop)[0]?.color : 'white'}
            locationId={notesData.locationId}
            date={notesData.date}
            isDateStatic={true}
            t={t}
          />
        </Panel>
      )}
    </div>
  );
};

export default Photo;
